<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Loader2 } from 'lucide-vue-next'
import { useAppStore } from '@/stores/app'
import { useAccountStore } from '@/stores'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'

const route = useRoute()
const { t } = useI18n()
const accountStore = useAccountStore()
const launching = ref(false)

const openInApp = async () => {
  launching.value = true
  try {
    await accountStore.openInApp()
  } finally {
    launching.value = false
  }
}

const handoffAppName = computed(() =>
  accountStore.handoffSource === 'sqlkit' ? t('sqlkitBrand.name') : t('dockitBrand.name'),
)

// auth entry pages — the handoff prompt must not cover the login flow itself
const AUTH_PATHS = ['/login', '/register', '/forgot-password', '/reset-password']

// The handoff prompt lives at the app root so any signed-in landing page can
// return the user to the client — a login that redirects to /subscribe
// (outside the dashboard layout) must not strand the handoff on the web.
let promptedHandoff = false
watch(
  () => [accountStore.handoffSource, accountStore.account.accessToken, route.path] as const,
  ([source, token, path]) => {
    if (!source) {
      promptedHandoff = false
      return
    }
    if (promptedHandoff || !token) return
    if (AUTH_PATHS.some((prefix) => path.startsWith(prefix))) return
    promptedHandoff = true
    accountStore.showHandoffConfirm = true
  },
  { immediate: true },
)

onMounted(() => {
  useAppStore().initTheme()
})
</script>

<template>
  <ScrollArea class="h-screen">
    <router-view v-slot="{ Component }">
      <transition name="fade" mode="out-in">
        <component :is="Component" />
      </transition>
    </router-view>
    <ScrollBar class="z-50" />
    <Dialog :open="accountStore.showHandoffConfirm" @update:open="accountStore.showHandoffConfirm = $event">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{{ t('handoff.loginSuccessful') }}</DialogTitle>
          <DialogDescription>
            {{ t('handoff.accountReady', { app: handoffAppName }) }}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" :disabled="launching" @click="accountStore.continueToWeb">{{
            t('handoff.continueToWeb')
          }}</Button>
          <Button class="bg-primary hover:bg-primary/90" :disabled="launching" @click="openInApp">
            <Loader2 v-if="launching" class="mr-2 h-4 w-4 animate-spin" />
            {{ launching ? t('dashboard.launching') : t('handoff.openInApp', { app: handoffAppName }) }}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </ScrollArea>
</template>
